import { FC, useMemo } from "react";
import {
  faGear,
  faMoneyBill,
  faMoneyBills,
  faScrewdriverWrench,
  faStore,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { faMegaphone } from "@fortawesome/pro-regular-svg-icons";
import { faCalendar } from "@fortawesome/free-regular-svg-icons";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { t } from "i18next";

import { Permission, usePermission } from "@/shared/hooks/usePermission";
import { SideBar } from "@/shared/components/molecules/SideBar";
import { selectUser } from "@/app/auth/auth.slice";
import { selectBranch } from "@/app/branchs/branch.slice";
import { DOMAIN } from "@/app/app.api";

export type BranchSideBarProps = {
  onBranchEdit: () => void;
  onBranchDelete: () => void;
};

const role_name = {
  ADMIN: "Admin",
  SUPER_ADMIN: "Super Admin",
  MANAGER: "Manager", // General manager
  TRAINER_PERSONAL: "Entrenador Personal",
  MAINTENANCE: "Mantenimeinto",
  RECEPTIONIST: "Recepcionista",
  COACH: "Coach",
  COMMERCIAL: "Comercial",
  CLIENT: "Cliente",
  SUPPORT: "Soporte",
  TESTER: "Tester2",
};

export const ROLE_NAMES = Object.freeze(role_name);
export type ROLE_NAME_TYPE = (typeof ROLE_NAMES)[keyof typeof ROLE_NAMES];

export const BranchSideBar: FC<BranchSideBarProps> = ({
  onBranchEdit,
  onBranchDelete,
}) => {
  const { id } = useParams();
  const user = useSelector(selectUser);
  const branch = useSelector(selectBranch);
  const { cant, can } = usePermission();

  const showSidebarBranch = user?.role !== ROLE_NAMES.MAINTENANCE;

  const hiddenChildrenSetting = useMemo(() => {
    const currentRole = user?.role as ROLE_NAME_TYPE;

    return {
      hidden_contracts_clauses_customers: ![
        ROLE_NAMES.ADMIN,
        ROLE_NAMES.SUPER_ADMIN,
        ROLE_NAMES.MANAGER,
      ].includes(currentRole),
      hidden_invoices: currentRole === ROLE_NAMES.TRAINER_PERSONAL,
      hidden_edit_delete_branch: currentRole !== ROLE_NAMES.SUPER_ADMIN,
    };
  }, [user]);

  const items = useMemo(() => {
    if (!branch) {
      return [];
    }
    return [
      {
        id: "personal",
        key: "personal",
        name: t("pages.personal.title"),
        href: `/branchs/${id}/personal`,
        current: false,
        icon: faUsers,
        hide: false, // always show for all roles except maintenance
      },
      {
        id: "accounting",
        key: "accounting",
        name: t("pages.accounting.title"),
        href: `/branchs/${id}/accounting`,
        current: false,
        icon: faMoneyBill,
        hide: !(
          branch.accounting_module === 1 &&
          [
            ROLE_NAMES.ADMIN,
            ROLE_NAMES.MANAGER,
            ROLE_NAMES.TRAINER_PERSONAL,
            ROLE_NAMES.RECEPTIONIST,
          ].includes(user?.role)
        ),
      },
      {
        id: "store",
        key: "store",
        name: t("store.views.title"),
        href: `/branchs/${id}/store`,
        current: false,
        icon: faStore,
        hide: branch.store === 0 || cant(Permission.USER_STORE_MANAGEMENT),
      },
      {
        id: "maintenance",
        key: "maintenance",
        name: t("maintenance.views.title"),
        href: `/branchs/${id}/maintenance`,
        current: false,
        icon: faScrewdriverWrench,
        hide:
          user?.role === ROLE_NAMES.TRAINER_PERSONAL &&
          cant(Permission.BRANCH_ACCESS_VIEW_MAINTENANCE),
      },
      {
        id: "calendar",
        key: "calendar",
        name: t("general.calendar"),
        href: `/branchs/${id}/events/`,
        current: false,
        icon: faCalendar,
        hide:
          branch.lesson_module === 0 &&
          branch.assisted_module === 0 &&
          branch.slots === 0 &&
          branch.course_module === 0,
        children: [
          {
            id: "lessons",
            key: "lessons",
            name: t("calendar.lessons"),
            href: `/branchs/${id}/events/directed-activities`,
            hide:
              branch?.lesson_module === 0 ||
              ![
                ROLE_NAMES.SUPER_ADMIN,
                ROLE_NAMES.MANAGER,
                ROLE_NAMES.COMMERCIAL,
                ROLE_NAMES.ADMIN,
                ROLE_NAMES.TRAINER_PERSONAL,
              ].includes(user?.role),
          },
          {
            id: "assisteds",
            key: "assisteds",
            name: t("calendar.assisteds"),
            href: `/branchs/${id}/events/professional-services`,
            hide:
              branch.assisted_module === 0 ||
              ![
                ROLE_NAMES.SUPER_ADMIN,
                ROLE_NAMES.MANAGER,
                ROLE_NAMES.COMMERCIAL,
                ROLE_NAMES.ADMIN,
                ROLE_NAMES.TRAINER_PERSONAL,
              ].includes(user?.role),
          },
          {
            id: "spaces",
            key: "spaces",
            name: "Espacios",
            href: `/branchs/${id}/events/spaces`,
            hide:
              branch.slots === 0 ||
              ![
                ROLE_NAMES.SUPER_ADMIN,
                ROLE_NAMES.MANAGER,
                ROLE_NAMES.COMMERCIAL,
                ROLE_NAMES.ADMIN,
                ROLE_NAMES.TRAINER_PERSONAL,
              ].includes(user?.role),
          },
          {
            id: "courses",
            key: "courses",
            name: "Cursos",
            href: `/branchs/${id}/events/courses`,
            hide:
              branch.course_module === 0 ||
              ![
                ROLE_NAMES.SUPER_ADMIN,
                ROLE_NAMES.MANAGER,
                ROLE_NAMES.COMMERCIAL,
                ROLE_NAMES.ADMIN,
                ROLE_NAMES.TRAINER_PERSONAL,
              ].includes(user?.role),
          },
        ],
      },
      {
        id: "fees",
        key: "fees",
        name: t("accounting.views.cuotas"),
        href: `/branchs/${id}/fees`,
        current: false,
        icon: faMoneyBills,
        hide:
          branch.customer_to_gym_subscription === 0 ||
          cant(Permission.BRANCH_ACCESS_VIEW_FEES),
      },
      {
        id: "marketing",
        key: "marketing",
        name: "Marketing",
        icon: faMegaphone,
        href: `/branchs/${id}/marketing`,
        hide: !(
          branch.uses_brand === 1 &&
          ![ROLE_NAMES.COACH, ROLE_NAMES.RECEPTIONIST].includes(user?.role)
        ),
        current: false,
        children: [
          {
            id: "captation",
            key: "captation",
            name: t("user.views.catchment_module"),
            href: `/branchs/${id}/marketing/captation`,
            hide:
              branch.catchment_module === 0 && user?.role !== ROLE_NAMES.COACH,
          },
          {
            id: "recovery",
            key: "recovery",
            name: t("recovery.views.recovery"),
            href: `/branchs/${id}/marketing/recovery`,
            hide:
              branch.recovery_module === 0 && user?.role !== ROLE_NAMES.COACH,
          },
          {
            id: "posts",
            key: "posts",
            name: "Mensajes masivos",
            href: `/branchs/${id}/marketing/posts`,
            hide:
              branch.post_module === 0 ||
              !can(
                Permission.BRANCH_ACCESS_CREATE_EDIT_ACTIVATE_INTERNAL_CHAT_POSTS
              ),
          },
          {
            id: "pre-recorded-classes",
            key: "pre-recorded-classes",
            name: t("additional-videos.views.title"),
            href: `/branchs/${id}/events/pre-recorded-classes`,
            hide: branch.additional_videos_module === 0,
          },
        ],
      },
      {
        id: "settings",
        key: "settings",
        name: t("share.views.settings"),
        href: `/branchs/${id}/setting`,
        icon: faGear,
        current: false,
        children: [
          {
            id: "marketing-tags",
            key: "marketing-tags",
            name: "Etiquetas de usuario / convenios de empresa y etiquetas de marketing",
            href: `/branchs/${id}/settings/marketing-tags`,
            hide: !can(
              Permission.BRANCH_ACCESS_CREATE_EDIT_USER_AND_MARKETING_TAGS // show this item by default but check if the user has the permission
            ),
          },
          {
            id: "contracts",
            key: "contracts",
            name: "Contratos y clausulas con clientes",
            href: `/branchs/${id}/settings/contracts`,
            hide: hiddenChildrenSetting.hidden_contracts_clauses_customers,
          },
          {
            id: "invoices",
            key: "invoices",
            name: t("share.views.impuestos_recibos_clientes"),
            href: `/branchs/${id}/settings/invoices`,
            hide: hiddenChildrenSetting.hidden_invoices,
          },
          {
            id: "customization",
            key: "customization",
            name: t("share.views.customization_logo_and_corporate_colors"),
            href: `/branchs/${id}/settings/customization`,
            hide:
              branch.logo_color_module === 0 ||
              cant(Permission.BRANCH_ACCESS_CUSTOMIZE_LOGO_AND_COLORS),
          },
          {
            id: "exercises",
            key: "exercises",
            name: t("brand.views.customize_exercise_videos"),
            href: `/branchs/${id}/settings/exercises`,
            hide: branch.videos_module === 0,
          },
          {
            id: "device-linking",
            key: "device-linking",
            name: "Vinculación de dispositivos",
            href: `/branchs/${id}/settings/device-linking`,
            hide: cant(Permission.BRANCH_ACCESS_DEVICE_LINKING),
          },
          {
            id: "accounting-integration",
            key: "accounting-integration",
            name: "Integración contabilidad",
            href: `/branchs/${id}/settings/accounting-integration`,
            hide: cant(Permission.BRANCH_ACCESS_ACCOUNTING_INTEGRATION),
          },
          {
            id: "authorization",
            key: "authorization",
            name: t("permissions.views.permi_roles"),
            href: `/branchs/${id}/settings/authorization`,
            hide: cant(
              Permission.BRANCH_ACCESS_CREATE_EDIT_ROLES_AND_PERMISSIONS
            ),
          },
          {
            id: "subscriptions",
            key: "subscriptions",
            name: t("pages.subscriptions.title"),
            href: `/branchs/${id}/settings/subscriptions`,
            hide: cant(Permission.BRANCH_ACCESS_FITMEWISE_SUBSCRIPTION),
          },
          {
            id: "edit",
            key: "edit",
            name: t("branch.views.edit"),
            onClick: onBranchEdit,
            hide: hiddenChildrenSetting.hidden_edit_delete_branch,
          },
          {
            id: "delete",
            key: "delete",
            name: t("branch.views.delete"),
            onClick: onBranchDelete,
            hide: hiddenChildrenSetting.hidden_edit_delete_branch,
          },
        ],
      },
    ];
  }, [id, branch, cant, onBranchEdit, onBranchDelete]);

  if (!branch) {
    return null;
  }

  if (!showSidebarBranch) {
    return null;
  }

  return (
    <>
      <SideBar
        items={items}
        logoUrl={
          branch?.logo
            ? `${DOMAIN}/uploads/branchs/${branch.logo}`
            : `https://dash.fitmewise.com/uploads/branchs/default.png`
        }
      />
    </>
  );
};
